/**
 * printReport.js - Utilidad unificada de impresión para KAPITAL
 * Abre una nueva pestaña con contenido HTML profesional para impresión
 * Basado en el patrón de DividendTrackerPro.jsx
 */

/**
 * Genera y abre una ventana de impresión con un informe profesional
 * @param {Object} config - Configuración del informe
 * @param {string} config.title - Título principal del informe (ej: "Informe de Dividendos")
 * @param {string} config.subtitle - Subtítulo opcional
 * @param {string} config.icon - Emoji para el título (ej: "💰", "📊")
 * @param {string} config.accentColor - Color principal en hex (ej: "#22c55e")
 * @param {Array} config.summaryCards - Array de tarjetas de resumen [{value, label, color?}]
 * @param {Array} config.tableHeaders - Headers de la tabla ["Ticker", "Nombre", ...]
 * @param {Array} config.tableRows - Filas de la tabla [[val1, val2, ...], ...]
 * @param {string} config.footnote - Nota al pie opcional
 * @param {Array} config.sections - Secciones adicionales [{title, content}]
 * @returns {boolean} - true si se abrió correctamente
 */
export const printReport = ({
  title = 'Informe KAPITAL',
  subtitle = '',
  icon = '📊',
  accentColor = '#0891b2',
  summaryCards = [],
  tableHeaders = [],
  tableRows = [],
  footnote = '',
  sections = []
}) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    return false;
  }

  const date = new Date().toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const logoUrl = `${window.location.origin}/logo-kapital-k.png`;

  // Generar HTML de tarjetas de resumen
  const summaryHTML = summaryCards.length > 0 ? `
    <div class="summary" style="grid-template-columns: repeat(${Math.min(summaryCards.length, 4)}, 1fr);">
      ${summaryCards.map(card => `
        <div class="summary-item">
          <div class="summary-value" style="color: ${card.color || accentColor};">${card.value}</div>
          <div class="summary-label">${card.label}</div>
        </div>
      `).join('')}
    </div>
  ` : '';

  // Generar HTML de la tabla
  const tableHTML = tableHeaders.length > 0 && tableRows.length > 0 ? `
    <table>
      <thead>
        <tr>
          ${tableHeaders.map(h => `<th>${h}</th>`).join('')}
        </tr>
      </thead>
      <tbody>
        ${tableRows.map(row => `
          <tr>
            ${row.map((cell, i) => `<td${i === 0 ? ' class="ticker"' : ''}>${cell}</td>`).join('')}
          </tr>
        `).join('')}
      </tbody>
    </table>
  ` : '';

  // Generar HTML de secciones adicionales
  const sectionsHTML = sections.map(section => `
    <div class="section">
      <h2>${section.title}</h2>
      ${section.content}
    </div>
  `).join('');

  // Calcular color de fondo claro basado en accentColor
  const lightBgColor = accentColor + '15';

  printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${title} - KAPITAL</title>
      <script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"></script>
      <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"></script>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        
        body { 
          font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif; 
          padding: 30px; 
          max-width: 1000px; 
          margin: 0 auto; 
          color: #1f2937;
          line-height: 1.6;
          background: #fff;
        }
        
        /* Header */
        /* Brand bar */
        .brand-bar {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 18px;
          margin-bottom: 18px;
          border-radius: 12px;
          background: linear-gradient(135deg, #0b1220 0%, #0f172a 60%, #082f36 100%);
          border: 1px solid ${accentColor}55;
        }
        .brand-logo {
          height: 44px;
          width: auto;
          filter: drop-shadow(0 0 6px ${accentColor}66);
        }
        .brand-text { display: flex; flex-direction: column; line-height: 1.1; }
        .brand-name {
          font-size: 22px;
          font-weight: 800;
          letter-spacing: 3px;
          color: #22d3ee;
        }
        .brand-tag { font-size: 11px; color: #94a3b8; letter-spacing: 1px; }

        .header {
          border-bottom: 3px solid ${accentColor};
          padding-bottom: 15px;
          margin-bottom: 25px;
        }
        .header h1 {
          color: ${accentColor};
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 5px;
        }
        .header .subtitle {
          color: #6b7280;
          font-size: 14px;
        }
        .header .date {
          color: #9ca3af;
          font-size: 12px;
          margin-top: 8px;
        }
        
        /* Summary Cards */
        .summary {
          display: grid;
          gap: 15px;
          margin: 25px 0;
          padding: 20px;
          background: ${lightBgColor};
          border-radius: 12px;
          border: 1px solid ${accentColor}30;
        }
        .summary-item {
          text-align: center;
          padding: 10px;
        }
        .summary-value {
          font-size: 26px;
          font-weight: 700;
        }
        .summary-label {
          font-size: 11px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 4px;
        }
        
        /* Tables */
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 25px 0;
          font-size: 13px;
        }
        th, td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
        }
        th {
          background: #f9fafb;
          font-weight: 600;
          color: #374151;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.5px;
        }
        tbody tr:hover {
          background: #f9fafb;
        }
        .ticker {
          font-weight: 700;
          color: ${accentColor};
        }
        
        /* Value colors */
        .positive { color: #16a34a; font-weight: 600; }
        .negative { color: #dc2626; font-weight: 600; }
        .neutral { color: #6b7280; }
        .highlight { color: ${accentColor}; font-weight: 700; }
        
        /* Sections */
        .section {
          margin: 30px 0;
          page-break-inside: avoid;
        }
        .section h2 {
          font-size: 18px;
          color: #374151;
          border-left: 4px solid ${accentColor};
          padding-left: 12px;
          margin-bottom: 15px;
        }
        
        /* Footer */
        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 2px solid #e5e7eb;
          text-align: center;
        }
        .footer p {
          font-size: 11px;
          color: #9ca3af;
          margin: 5px 0;
        }
        .footer .brand {
          font-weight: 700;
          color: ${accentColor};
        }
        
        /* Barra de acciones (no se imprime) */
        .toolbar {
          position: sticky;
          top: 0;
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          padding: 12px 0 16px;
          background: #fff;
          z-index: 10;
        }
        .toolbar button {
          font-family: inherit;
          font-size: 14px;
          font-weight: 600;
          padding: 10px 18px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          transition: opacity .2s, transform .1s;
        }
        .toolbar button:active { transform: scale(0.97); }
        .btn-pdf { background: ${accentColor}; color: #fff; }
        .btn-print { background: #f1f5f9; color: #334155; border: 1px solid #cbd5e1; }
        .toolbar button:hover { opacity: 0.9; }
        .btn-pdf[disabled] { opacity: 0.6; cursor: wait; }

        /* Print styles */
        @media print {
          .no-print { display: none !important; }
          body { 
            padding: 15px; 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .summary { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .section { page-break-inside: avoid; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; }
        }
        
        /* Mobile responsive */
        @media (max-width: 600px) {
          body { padding: 15px; }
          .summary { grid-template-columns: repeat(2, 1fr) !important; }
          .summary-value { font-size: 20px; }
          table { font-size: 11px; }
          th, td { padding: 8px; }
        }
      </style>
    </head>
    <body>
      <div class="toolbar no-print">
        <button class="btn-pdf" id="downloadPdfBtn" onclick="descargarPDF()">⬇️ Descargar PDF</button>
        <button class="btn-print" onclick="window.print()">🖨️ Imprimir</button>
      </div>
      <div id="report-content">
        <div class="brand-bar">
          <img src="${logoUrl}" alt="KAPITAL" class="brand-logo" crossorigin="anonymous" onerror="this.style.display='none'" />
          <div class="brand-text">
            <span class="brand-name">KAPITAL</span>
            <span class="brand-tag">Análisis Técnico de Activos</span>
          </div>
        </div>
        <div class="header">
          <h1>${icon} ${title}</h1>
          ${subtitle ? `<p class="subtitle">${subtitle}</p>` : ''}
          <p class="date">Generado: ${date}</p>
        </div>

        ${summaryHTML}
        ${tableHTML}
        ${sectionsHTML}

        <div class="footer">
          <p>Documento generado por <span class="brand">KAPITAL</span> - Análisis Técnico de Activos</p>
          ${footnote ? `<p>⚠️ ${footnote}</p>` : '<p>⚠️ Este documento es solo informativo y no constituye asesoramiento financiero.</p>'}
        </div>
      </div>
      <script>
        function descargarPDF() {
          var btn = document.getElementById('downloadPdfBtn');
          var el = document.getElementById('report-content');
          if (!window.html2canvas || !window.jspdf) {
            alert('No se pudo cargar el generador de PDF. Usa "Imprimir" y elige "Guardar como PDF".');
            return;
          }
          var original = btn.innerHTML;
          btn.disabled = true; btn.innerHTML = '⏳ Generando…';
          window.html2canvas(el, { scale: 2, backgroundColor: '#ffffff', useCORS: true }).then(function(canvas) {
            var imgData = canvas.toDataURL('image/jpeg', 0.95);
            var jsPDF = window.jspdf.jsPDF;
            var pdf = new jsPDF('p', 'mm', 'a4');
            var pageW = pdf.internal.pageSize.getWidth();
            var pageH = pdf.internal.pageSize.getHeight();
            var imgW = pageW;
            var imgH = canvas.height * imgW / canvas.width;
            var heightLeft = imgH;
            var position = 0;
            pdf.addImage(imgData, 'JPEG', 0, position, imgW, imgH);
            heightLeft -= pageH;
            while (heightLeft > 0) {
              position -= pageH;
              pdf.addPage();
              pdf.addImage(imgData, 'JPEG', 0, position, imgW, imgH);
              heightLeft -= pageH;
            }
            var fname = ${JSON.stringify(title)}.replace(/[^a-z0-9]+/gi, '_') + '_' + new Date().toISOString().slice(0,10) + '.pdf';
            pdf.save(fname);
            btn.disabled = false; btn.innerHTML = original;
          }).catch(function(err) {
            btn.disabled = false; btn.innerHTML = original;
            alert('No se pudo generar el PDF. Usa "Imprimir" y elige "Guardar como PDF".');
          });
        }
      </script>
    </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();

  return true;
};

/**
 * Formatea un número como moneda EUR
 */
export const formatCurrency = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return '€' + Number(value).toLocaleString('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Formatea un número como porcentaje
 */
export const formatPercent = (value, decimals = 2, showSign = false) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  const formatted = Number(value).toFixed(decimals) + '%';
  return showSign && value > 0 ? '+' + formatted : formatted;
};

/**
 * Genera clase CSS para valores positivos/negativos
 */
export const getValueClass = (value) => {
  if (value > 0) return 'positive';
  if (value < 0) return 'negative';
  return 'neutral';
};

/**
 * Formatea valor con clase de color HTML
 */
export const formatValueWithColor = (value, format = 'currency') => {
  const cls = getValueClass(value);
  let formatted;
  
  if (format === 'currency') {
    formatted = formatCurrency(value);
    if (value > 0) formatted = '+' + formatted;
  } else if (format === 'percent') {
    formatted = formatPercent(value, 2, true);
  } else {
    formatted = value;
  }
  
  return `<span class="${cls}">${formatted}</span>`;
};

export default { printReport, formatCurrency, formatPercent, getValueClass, formatValueWithColor };
